/**
 * Dose calculator component — verbatim visual port from the standalone app.
 *
 * Behaviour preserved, math delegated to `@reefapp/shared/dose-math`.
 * Enhancements added:
 *   (A) Tank volume persisted on the Pi (`stock-tank-gallons` namespace,
 *       legacy localStorage value as the instant-on seed)
 *   (C) Audit-log call on each calculation
 *   (F) Apex Trident probe autofill pill (read-only suggestion)
 *   (G) Gallons ↔ Litres toggle
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  NitrateForecastResult,
  ParameterSolution,
  RatioSolution,
  Solution,
  VolumeUnit,
} from '@reefapp/shared';
import {
  computeCorrectionDoseFromStrings,
  computeDailyDoseFromStrings,
  computeNitrateForecastFromStrings,
  computeRatioDoseFromStrings,
  gallonsToLiters,
  litersToGallons,
} from '@reefapp/shared';


import { useServerBackedState } from '../hooks/useServerBackedState';
import { writeStockSolutionLog } from '../lib/stock-solutions-log';
import { useApexProbeForSolution } from './useApexProbeForSolution';
import { UnitsToggle } from './UnitsToggle';
import { useUnitPreference } from './useUnitPreference';
import type { PrefillFromDeepLink } from './types';

/** Legacy browser-only key — read as the seed default, never deleted. */
const LEGACY_TANK_VOLUME_KEY = 'reefapp.stockSolutions.tankGallons';

/** `/api/userdata` namespace holding the tank volume (gallons) on the Pi. */
export const TANK_GALLONS_NAMESPACE = 'stock-tank-gallons';

interface CalculatorProps {
  solution: Solution;
  prefill?: PrefillFromDeepLink;
}

/** Reads the legacy stored gallons — raw string from localStorage. */
function legacyTankGallons(): string {
  try {
    return window.localStorage.getItem(LEGACY_TANK_VOLUME_KEY) ?? '';
  } catch {
    return '';
  }
}

/**
 * The migration may have uploaded the legacy raw string as a JSON
 * number — accept both shapes.
 */
function asGallonsString(value: string | number): string {
  return typeof value === 'number' ? String(value) : value;
}

function parseFinite(raw: string): number | null {
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return n;
}

export function DoseCalculator({ solution, prefill }: CalculatorProps) {
  const { unit, setUnit, loading: unitLoading } = useUnitPreference();

  // Tank volume (gallons) lives on the Pi; the legacy localStorage
  // value is the instant-on seed while the round-trip is in flight.
  const [legacyGallons] = useState(legacyTankGallons);
  const {
    value: storedTankValue,
    setValue: saveTankGallons,
    loading: tankLoading,
  } = useServerBackedState<string | number>(
    TANK_GALLONS_NAMESPACE,
    legacyGallons,
  );
  const storedGallonsFromPi = asGallonsString(storedTankValue);

  // The displayed tank volume string (in the user's chosen unit).
  const [tankVolumeDisplay, setTankVolumeDisplay] = useState(() => {
    const storedGallons = legacyTankGallons();
    if (unit === 'L' && storedGallons !== '') {
      const n = parseFinite(storedGallons);
      if (n !== null) return String(gallonsToLiters(n));
    }
    return storedGallons;
  });
  // Set once the user types — hydration must never clobber their input,
  // and we only persist values the user actually entered.
  const userEditedTankVolume = useRef(false);

  const [currentLevel, setCurrentLevel] = useState('');
  const [targetLevel, setTargetLevel] = useState('');
  const [result, setResult] = useState<string | null>(null);

  const [dailyConsumption, setDailyConsumption] = useState('');
  const [dailyResult, setDailyResult] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'correction' | 'daily'>('correction');

  // Task B: nitrate forecast (ammonia-based nitrogen sources only)
  const [nitratePlannedDose, setNitratePlannedDose] = useState('');
  const [nitrateCurrentLevel, setNitrateCurrentLevel] = useState('');
  const [nitrateResult, setNitrateResult] = useState<NitrateForecastResult | null>(null);

  // Enhancement F: live probe reading
  const probe = useApexProbeForSolution(solution);

  const isParameterBased = solution.kind === 'parameter';
  const isRatioBased = solution.kind === 'ratio';
  const showNitrateForecast =
    solution.kind === 'parameter' && solution.nitrifiesToNitrate === true;


  // Derived: tank volume in gallons as string (the value passed to math).
  const tankGallonsString = useMemo(() => {
    const displayNum = parseFinite(tankVolumeDisplay);
    if (displayNum === null) return '';
    if (unit === 'L') {
      return String(litersToGallons(displayNum));
    }
    return tankVolumeDisplay;
  }, [tankVolumeDisplay, unit]);

  // One-time hydration: once the Pi value (and unit preference) have
  // loaded, adopt the stored gallons — unless the user already typed.
  const tankHydrated = useRef(false);
  useEffect(() => {
    if (tankHydrated.current) return;
    if (tankLoading || unitLoading) return;
    tankHydrated.current = true;
    if (userEditedTankVolume.current) return;
    if (storedGallonsFromPi === '') return;
    if (unit === 'L') {
      const n = parseFinite(storedGallonsFromPi);
      setTankVolumeDisplay(
        n !== null ? String(gallonsToLiters(n)) : storedGallonsFromPi,
      );
    } else {
      setTankVolumeDisplay(storedGallonsFromPi);
    }
  }, [tankLoading, unitLoading, storedGallonsFromPi, unit]);

  // Persist tank volume in gallons on user change (Enhancement A + G) —
  // write-through to the Pi + localStorage mirror.
  useEffect(() => {
    if (!userEditedTankVolume.current) return;
    saveTankGallons(tankGallonsString);
  }, [tankGallonsString, saveTankGallons]);

  const handleTankVolumeInput = useCallback((raw: string) => {
    userEditedTankVolume.current = true;
    setTankVolumeDisplay(raw);
  }, []);

  // When unit toggles, convert the displayed value if it's a valid number
  const handleUnitChange = useCallback(
    (newUnit: VolumeUnit) => {
      const currentNum = parseFinite(tankVolumeDisplay);
      if (currentNum !== null) {
        if (newUnit === 'L' && unit === 'gal') {
          setTankVolumeDisplay(String(gallonsToLiters(currentNum)));
        } else if (newUnit === 'gal' && unit === 'L') {
          setTankVolumeDisplay(String(litersToGallons(currentNum)));
        }
      }
      setUnit(newUnit);
    },
    [tankVolumeDisplay, unit, setUnit],
  );

  // If unit preference changed externally (between mounts), we need to
  // convert. But since the hook reads on mount, and the initializer
  // handles it, this is only needed if unit changes while the component
  // is mounted (which only happens via handleUnitChange above).

  // Pre-fill from deep-link on mount only
  const prefillApplied = useRef(false);
  useEffect(() => {
    if (!prefill || prefillApplied.current) return;
    prefillApplied.current = true;
    if (prefill.current && currentLevel === '') {
      setCurrentLevel(prefill.current);
    }
    if (prefill.target && targetLevel === '') {
      setTargetLevel(prefill.target);
    }
  }, [prefill, currentLevel, targetLevel]);

  // Reset fields when solution changes
  useEffect(() => {
    setCurrentLevel('');
    setTargetLevel('');
    setResult(null);
    setDailyConsumption('');
    setDailyResult(null);
    setActiveTab('correction');
    setNitratePlannedDose('');
    setNitrateCurrentLevel('');
    setNitrateResult(null);
    prefillApplied.current = false;
  }, [solution]);


  const handleProbeFill = useCallback(() => {
    if (probe.value !== null) {
      setCurrentLevel(String(probe.value));
    }
  }, [probe]);

  const handleCalculateCorrection = () => {
    setDailyResult(null);

    if (isRatioBased) {
      const r = computeRatioDoseFromStrings({
        solution: solution as RatioSolution,
        tankGallonsRaw: tankGallonsString,
      });
      if (!r.ok) {
        setResult(r.reason);
        return;
      }
      const msg = `Recommended Dose: ${r.mL.toFixed(2)} mL`;
      setResult(msg);
      writeStockSolutionLog({
        slug: solution.slug,
        tankGallons: tankGallonsString,
        doseML: r.mL,
        mode: 'ratio',
      });
      return;
    }

    if (isParameterBased) {
      const r = computeCorrectionDoseFromStrings({
        solution: solution as ParameterSolution,
        tankGallonsRaw: tankGallonsString,
        currentLevelRaw: currentLevel,
        targetLevelRaw: targetLevel,
      });
      if (!r.ok) {
        setResult(r.reason);
        return;
      }
      const msg = `Recommended Dose: ${r.mL.toFixed(2)} mL`;
      setResult(msg);
      writeStockSolutionLog({
        slug: solution.slug,
        tankGallons: tankGallonsString,
        doseML: r.mL,
        mode: 'correction',
        currentLevel,
        targetLevel,
        units: (solution as ParameterSolution).units,
      });
      return;
    }

    setResult('Calculation not available for this supplement.');
  };

  const handleCalculateDaily = () => {
    setResult(null);

    if (!isParameterBased) {
      setDailyResult('Calculation not available for this supplement.');
      return;
    }

    const r = computeDailyDoseFromStrings({
      solution: solution as ParameterSolution,
      tankGallonsRaw: tankGallonsString,
      dailyConsumptionRaw: dailyConsumption,
    });
    if (!r.ok) {
      setDailyResult(r.reason);
      return;
    }
    const msg = `Recommended Daily Dose: ${r.mL.toFixed(2)} mL`;
    setDailyResult(msg);
    writeStockSolutionLog({
      slug: solution.slug,
      tankGallons: tankGallonsString,
      doseML: r.mL,
      mode: 'daily',
      dailyConsumption,
      units: (solution as ParameterSolution).units,
    });
  };

  const handleCalculateNitrate = () => {
    if (solution.kind !== 'parameter') return;
    const r = computeNitrateForecastFromStrings({
      solution,
      tankGallonsRaw: tankGallonsString,
      doseMLRaw: nitratePlannedDose,
      currentNitrateRaw: nitrateCurrentLevel,
    });
    setNitrateResult(r);
  };

  const volumeLabel = unit === 'L' ? 'System Volume (Liters):' : 'System Volume (US Gal):';


  const correctionFields = (
    <div>
      <div className={`grid grid-cols-1 ${isRatioBased ? '' : 'sm:grid-cols-3'} gap-4`}>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <label htmlFor="s-tank-volume" className="block text-sm font-medium text-slate-300">
              {volumeLabel}
            </label>
            <UnitsToggle unit={unit} onChange={handleUnitChange} />
          </div>
          <input
            type="number"
            id="s-tank-volume"
            value={tankVolumeDisplay}
            onChange={(e) => handleTankVolumeInput(e.target.value)}
            placeholder={unit === 'L' ? 'e.g., 380' : 'e.g., 100'}
            className="w-full bg-slate-700 border border-slate-600 rounded-md py-2 px-3 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
          />
        </div>
        {!isRatioBased && (
          <>
            <div>
              <label htmlFor="s-current-level" className="block text-sm font-medium text-slate-300 mb-1">
                Current Level ({(solution as ParameterSolution).units}):
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  id="s-current-level"
                  value={currentLevel}
                  onChange={(e) => setCurrentLevel(e.target.value)}
                  placeholder="e.g., 7.5"
                  className="w-full bg-slate-700 border border-slate-600 rounded-md py-2 px-3 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>
              {/* Enhancement F: probe autofill pill */}
              {probe.value !== null && (
                <button
                  type="button"
                  onClick={handleProbeFill}
                  title={`Use live Trident reading: ${probe.value} ${probe.unit ?? ''}`}
                  className={`mt-1 text-xs font-medium px-2 py-0.5 rounded-full transition-colors
                    ${probe.stale
                      ? 'bg-amber-900/50 text-amber-400 border border-amber-600/40'
                      : 'bg-cyan-900/40 text-cyan-300 border border-cyan-600/40 hover:bg-cyan-800/50'
                    }`}
                >
                  {probe.stale ? '\u26A0 ' : '\u21BB '}
                  Live: {probe.value} {probe.unit ?? ''}
                </button>
              )}
            </div>
            <div>
              <label htmlFor="s-target-level" className="block text-sm font-medium text-slate-300 mb-1">
                Target Level ({(solution as ParameterSolution).units}):
              </label>
              <input
                type="number"
                id="s-target-level"
                value={targetLevel}
                onChange={(e) => setTargetLevel(e.target.value)}
                placeholder="e.g., 8.5"
                className="w-full bg-slate-700 border border-slate-600 rounded-md py-2 px-3 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </>
        )}
      </div>
      <button
        onClick={handleCalculateCorrection}
        className="mt-5 w-full bg-cyan-600 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-cyan-700 transition-colors duration-200"
      >
        Calculate Dose
      </button>
      {result && (
        <div className="mt-4 p-4 rounded-lg bg-slate-800 border border-cyan-500 text-center">
          <p className="font-bold text-lg text-cyan-300">{result}</p>
        </div>
      )}
    </div>
  );

  const dailyFields = (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <label htmlFor="d-tank-volume" className="block text-sm font-medium text-slate-300">
              {volumeLabel}
            </label>
            <UnitsToggle unit={unit} onChange={handleUnitChange} />
          </div>
          <input
            type="number"
            id="d-tank-volume"
            value={tankVolumeDisplay}
            onChange={(e) => handleTankVolumeInput(e.target.value)}
            placeholder={unit === 'L' ? 'e.g., 380' : 'e.g., 100'}
            className="w-full bg-slate-700 border border-slate-600 rounded-md py-2 px-3 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
          />
        </div>
        <div>
          <label htmlFor="d-daily-consumption" className="block text-sm font-medium text-slate-300 mb-1">
            Daily Consumption ({(solution as ParameterSolution).units}):
          </label>
          <input
            type="number"
            id="d-daily-consumption"
            value={dailyConsumption}
            onChange={(e) => setDailyConsumption(e.target.value)}
            placeholder="e.g., 0.5"
            className="w-full bg-slate-700 border border-slate-600 rounded-md py-2 px-3 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
          />
        </div>
      </div>
      <button
        onClick={handleCalculateDaily}
        className="mt-5 w-full bg-cyan-600 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-cyan-700 transition-colors duration-200"
      >
        Calculate Daily Dose
      </button>
      {dailyResult && (
        <div className="mt-4 p-4 rounded-lg bg-slate-800 border border-cyan-500 text-center">
          <p className="font-bold text-lg text-cyan-300">{dailyResult}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="mt-6 bg-slate-900 p-6 rounded-xl border border-slate-700">
      <h3 className="text-xl font-semibold text-cyan-300 mb-4">Dose Calculator</h3>

      {isParameterBased && (
        <div className="mb-4 flex border-b border-slate-600">
          <button
            onClick={() => setActiveTab('correction')}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${
              activeTab === 'correction'
                ? 'bg-slate-700 text-cyan-300 border-b-2 border-cyan-400'
                : 'text-slate-400 hover:bg-slate-800'
            }`}
          >
            One-Time Correction
          </button>
          <button
            onClick={() => setActiveTab('daily')}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${
              activeTab === 'daily'
                ? 'bg-slate-700 text-cyan-300 border-b-2 border-cyan-400'
                : 'text-slate-400 hover:bg-slate-800'
            }`}
          >
            Daily Dosing
          </button>
        </div>
      )}

      {isRatioBased && correctionFields}
      {isParameterBased && activeTab === 'correction' && correctionFields}
      {isParameterBased && activeTab === 'daily' && dailyFields}

      {showNitrateForecast && (
        <div className="mt-6 border-t border-slate-700 pt-6">
          <h3 className="text-xl font-semibold text-emerald-300 mb-1">Nitrate Forecast</h3>
          <p className="text-sm text-slate-400 mb-4">
            The ammonia you dose is converted by your tank&apos;s bacteria into nitrate. Enter a
            planned dose and your latest nitrate reading to see how much nitrate it adds and where
            your tank will land. Uses the System Volume entered above.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="n-planned-dose" className="block text-sm font-medium text-slate-300 mb-1">
                Planned Dose (mL):
              </label>
              <input
                type="number"
                id="n-planned-dose"
                value={nitratePlannedDose}
                onChange={(e) => setNitratePlannedDose(e.target.value)}
                placeholder="e.g., 5"
                className="w-full bg-slate-700 border border-slate-600 rounded-md py-2 px-3 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div>
              <label htmlFor="n-current-nitrate" className="block text-sm font-medium text-slate-300 mb-1">
                Current Nitrate (ppm):
              </label>
              <input
                type="number"
                id="n-current-nitrate"
                value={nitrateCurrentLevel}
                onChange={(e) => setNitrateCurrentLevel(e.target.value)}
                placeholder="e.g., 5"
                className="w-full bg-slate-700 border border-slate-600 rounded-md py-2 px-3 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>
          <button
            onClick={handleCalculateNitrate}
            className="mt-5 w-full bg-emerald-600 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-emerald-700 transition-colors duration-200"
          >
            Forecast Nitrate
          </button>
          {nitrateResult && !nitrateResult.ok && (
            <div className="mt-4 p-4 rounded-lg bg-slate-800 border border-amber-500 text-center">
              <p className="font-semibold text-amber-300">{nitrateResult.reason}</p>
            </div>
          )}
          {nitrateResult && nitrateResult.ok && (
            <div className="mt-4 p-4 rounded-lg bg-slate-800 border border-emerald-500 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-300">Ammonia (NH3) added:</span>
                <span className="font-mono text-emerald-300">
                  +{nitrateResult.ammoniaRaise.toFixed(3)} ppm
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-300">Nitrate (NO3) produced:</span>
                <span className="font-mono text-emerald-300">
                  +{nitrateResult.nitrateRaise.toFixed(3)} ppm
                </span>
              </div>
              <div className="flex justify-between text-base pt-2 mt-1 border-t border-slate-700">
                <span className="font-semibold text-slate-200">Projected Nitrate:</span>
                <span className="font-mono font-bold text-emerald-300">
                  {nitrateResult.projectedNitrate.toFixed(2)} ppm
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

